import React, { useId } from 'react'
import type { RadioProps } from './Radio.types'
import { useRadioGroupContext } from './RadioContext'
import './Radio.css'

const Radio: React.FC<RadioProps> = ({
  label,
  value,
  checked,
  defaultChecked,
  disabled,
  darkMode = false,
  previewState,
  className = '',
  id,
  name,
  onChange,
  ...rest
}) => {
  const generatedId = useId()
  const inputId = id ?? generatedId
  const group = useRadioGroupContext()
  const isPreview = previewState != null
  const isDisabled = disabled || group?.disabled || previewState === 'disabled'
  const isDark = darkMode || Boolean(group?.darkMode)
  const isGroupManaged = group != null
  const isChecked = group
    ? group.selectedValue === value
    : Boolean(checked ?? defaultChecked)

  const classNames = [
    'ds-radio',
    isChecked ? 'ds-radio--checked' : '',
    isDisabled ? 'ds-radio--disabled' : '',
    isDark ? 'ds-radio--dark' : '',
    isPreview ? 'ds-radio--preview' : '',
    previewState ? `ds-radio--preview-${previewState}` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (isPreview || isDisabled) {
      return
    }

    group?.onValueChange?.(value)
    onChange?.(event)
  }

  const inputProps =
    isGroupManaged || checked !== undefined
      ? { checked: isChecked }
      : { defaultChecked }

  return (
    <label className={classNames} htmlFor={inputId}>
      <input
        id={inputId}
        type="radio"
        className="ds-radio__input"
        name={group?.name ?? name}
        value={value}
        disabled={isDisabled}
        data-preview-state={previewState}
        onChange={handleChange}
        {...inputProps}
        {...rest}
      />
      <span className="ds-radio__control" aria-hidden="true">
        {isChecked ? <span className="ds-radio__dot" /> : null}
      </span>
      {label != null && label !== '' ? (
        <span className="ds-radio__label">{label}</span>
      ) : null}
    </label>
  )
}

export default Radio
